import React from "react";
import { MapPin, Pencil, Trash2, Users } from "lucide-react";
import UIButton from "../UIButton";
import { formatDate, formatMoney, statusClasses } from "../../hooks/useProjectsData";

export default function ProjectCard({
  project,
  selected,
  theme,
  text,
  onSelect,
  onEdit,
  onDelete,
}) {
  const projectProfit = project.budget - project.laborCost - project.materialCost - project.otherCost;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`w-full cursor-pointer rounded-[28px] border p-4 text-left transition md:p-5 ${
        selected
          ? theme === "dark"
            ? "border-blue-400/30 bg-blue-500/10 shadow-[0_0_0_1px_rgba(96,165,250,0.18)]"
            : "border-blue-200 bg-blue-50/70 shadow-[0_0_0_1px_rgba(96,165,250,0.08)]"
          : theme === "dark"
          ? "border-slate-800 bg-slate-900/60 hover:border-slate-700"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${statusClasses(project.status, theme)}`}>
              {text[project.status]}
            </span>
            <span className={`text-xs font-semibold ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
              {text.createdAt}: {formatDate(project.createdAt)}
            </span>
          </div>

          <div>
            <h3 className="truncate text-lg font-black md:text-xl">{project.name || "—"}</h3>
            <div className={`mt-1 flex flex-wrap items-center gap-3 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                {project.clientName || "—"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {project.address || "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 gap-2 self-start">
          <UIButton
            type="button"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(project);
            }}
            className="h-11 w-11 p-0"
          >
            <Pencil className="h-4 w-4" />
          </UIButton>

          <UIButton
            type="button"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(project);
            }}
            className="h-11 w-11 p-0"
          >
            <Trash2 className="h-4 w-4" />
          </UIButton>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <MiniMetric label={text.budget} value={formatMoney(project.budget)} theme={theme} />
        <MiniMetric
          label={text.totalCost}
          value={formatMoney(project.laborCost + project.materialCost + project.otherCost)}
          theme={theme}
        />
        <MiniMetric
          label={text.estimatedProfit}
          value={formatMoney(projectProfit)}
          theme={theme}
          positive={projectProfit >= 0}
        />
      </div>
    </div>
  );
}

export function MiniMetric({ label, value, theme, positive }) {
  return (
    <div className={`rounded-3xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white"}`}>
      <div className={`text-xs font-bold uppercase tracking-[0.18em] ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>{label}</div>
      <div className={`mt-2 text-lg font-black ${positive === undefined ? "" : positive ? theme === "dark" ? "text-emerald-300" : "text-emerald-700" : theme === "dark" ? "text-rose-300" : "text-rose-700"}`}>{value}</div>
    </div>
  );
}
